import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { FileText } from 'lucide-react';

const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO title="Terms of Use" description="Terms and conditions for using the Manage Your Loans website." path="/terms-of-use" />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#00594C] to-[#003d35] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <FileText className="w-8 h-8 text-[#99DF63]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Use</h1>
            <p className="text-lg text-gray-200">
              The terms governing your use of our website
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto prose prose-lg prose-gray">

            <p className="text-gray-600 leading-relaxed">
              This website is owned and operated by Manage Your Loans Pty Ltd ABN 34 652 442 790 ('we' 'us' and 'our').
            </p>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-4">1. Accepting these Terms of Use</h2>
            <p className="text-gray-600 leading-relaxed">
              By using our website, you confirm that you accept these Terms of Use and agree to be bound by them.
            </p>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-4">2. Our Website & Social Media Platforms</h2>
            <p className="text-gray-600 leading-relaxed">
              2.1 You must use our website and social media platforms in accordance with these Terms of Use, only for lawful purposes and only in a way that does not infringe the rights of anyone or restrict or inhibit anyone's use of our website and social media platforms.
            </p>
            <p className="text-gray-600 leading-relaxed">
              2.2 The material on the Website is published by us and is intended for use in Australia. The Website is not intended for use outside Australia. We make no claim that the information contained on this Website is appropriate or may be downloaded legally outside Australia.
            </p>
            <p className="text-gray-600 leading-relaxed">
              2.3 The Website is not intended for persons under the age of 18. If you are under 18, you should speak to your parents, your guardian, or a responsible adult and obtain their permission to use the Website.
            </p>
            <p className="text-gray-600 leading-relaxed">
              2.4 You understand and agree that your use of our website is provided "as is" and "as available". We do not represent or warrant that the operation of our website will be secure, confidential, uninterrupted, error-free, accurate, complete or current.
            </p>
            <p className="text-gray-600 leading-relaxed">
              2.5 We update and carry out maintenance on our website regularly, at times we may have to suspend access, service or functionality on our website, without notice. We will not be liable if, for any reason, our website is not available at any time or for any period of time.
            </p>
            <p className="text-gray-600 leading-relaxed">
              2.6 Our website contains links to third party sites, information and resources. We are not responsible for the Third Party websites or the information they contain, and cannot guarantee that any Third Party websites will be uninterrupted, error-free, accurate, complete or current.
            </p>
            <p className="text-gray-600 leading-relaxed">
              2.7 Any links to Third Parties does not represent our endorsement of such Third Party. We have no control over the Third Party content, and will not be liable for any loss or damage you suffer or incur when you access or use any Third Party websites or content.
            </p>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-4">3. Content</h2>
            <p className="text-gray-600 leading-relaxed">
              3.1 We own the intellectual property rights in the content of our website and social media platforms, including text, images and designs.
            </p>
            <p className="text-gray-600 leading-relaxed">
              3.2 Subject to clause 3.3, you retain all the rights in and to the content that you submit or post on our website.
            </p>
            <p className="text-gray-600 leading-relaxed">3.3 By submitting and posting your content on our website and social media platforms:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600">
              <li>you give us a perpetual, irrevocable, worldwide, royalty-free and non-exclusive licence to reproduce, publish, display and distribute your content;</li>
              <li>you consent to us disclosing your content and personal information with our suppliers; and</li>
              <li>you consent to us and our suppliers using your personal information to send you information about products and services that we feel may be of interest to you. You can unsubscribe from receiving this information at any time by emailing us at admin@manageyourloans.com.au.</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-4">4. General Advice Warning</h2>
            <p className="text-gray-600 leading-relaxed">
              4.1 The information presented on this website and our social media platforms is general in nature and does not take into account your personal goals, objectives and financial situation. It does not represent financial product advice. You should always seek independent legal and financial advice before making a decision in relation to a financial product. Read the Product Disclosure Statement (PDS) relevant to the product before making a decision.
            </p>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-4">5. Cookies</h2>
            <p className="text-gray-600 leading-relaxed">
              5.1 We use 'cookies' to provide you with a better service and a more effective website.
            </p>
            <p className="text-gray-600 leading-relaxed">
              5.2 A 'cookie' is a file placed on your computer by our web page server. A cookie can later be retrieved by our webpage servers. Cookies are frequently used on websites and you can choose if and how a cookie will be accepted by configuring your preferences and options in your internet browser.
            </p>
            <p className="text-gray-600 leading-relaxed">5.3 We use cookies for different purposes such as:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600">
              <li>to allocate a unique number to your internet browsers;</li>
              <li>to customise our website for you;</li>
              <li>for statistical purposes;</li>
              <li>to identify if you have accessed a Third Party Website; and</li>
              <li>for security purposes.</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-4">6. IP Addresses</h2>
            <p className="text-gray-600 leading-relaxed">
              6.1 Your IP address is the unique identifier for your computer when you are using the internet. They're used to identify the network you're connected to and the device you're using — allowing websites and other networks to route data to you, check your permissions, view your geolocation, and more.
            </p>
            <p className="text-gray-600 leading-relaxed">
              6.2 We may collect your IP address for your interaction with various parts of our website.
            </p>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-4">7. Advertising and tracking</h2>
            <p className="text-gray-600 leading-relaxed">
              7.1 When you view our advertisements on a Third Party website or social media platforms, the advertising company may use 'cookies' or other techniques to collect information such as the server your computer is logged onto, your browser type, the date and time of your visit, and the performance of their marketing efforts.
            </p>
            <p className="text-gray-600 leading-relaxed">
              7.2 When you access our website after viewing our advertisements on a Third Party website or social media platform, the advertising company collects information on how you utilise our website and whether you complete an online application.
            </p>
            <p className="text-gray-600 leading-relaxed">
              7.3 This information may be used to provide consented marketing to you in the future. If you do not wish to receive the marketing material you can unsubscribe at any time.
            </p>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-4">8. Online applications</h2>
            <p className="text-gray-600 leading-relaxed">
              8.1 When you send an online application to us, we retain the information contained in that application. We are able to then use that information to provide any financial services that you require.
            </p>
            <p className="text-gray-600 leading-relaxed">
              8.2 You can also suspend and save online applications, so you can complete and send the applications at a later time. Online applications that have been suspended or saved may be viewed by us.
            </p>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-4">9. Security of information</h2>
            <p className="text-gray-600 leading-relaxed">
              9.1 The security of your information is very important to us. We regularly stay abreast of best practice security and encryption technologies.
            </p>
            <p className="text-gray-600 leading-relaxed">
              9.2 We take all reasonable steps to protect the information in our systems from misuse, interference, loss, and any unauthorised access, modification or disclosure.
            </p>
            <p className="text-gray-600 leading-relaxed">
              9.3 If we no longer require your information, and we are legally permitted to, we will take all reasonable steps to destroy or de-identify the information.
            </p>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-4">10. Disclosure to overseas entities</h2>
            <p className="text-gray-600 leading-relaxed">
              10.1 We do not generally disclose personal information obtained from cookies to overseas entities in the course of our activities.
            </p>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-4">11. Liability</h2>
            <p className="text-gray-600 leading-relaxed">
              11.1 Except where we are unable to exclude our liability by law, we will not be liable to you for any loss or damage, however it arises, whether in contract, statute or tort (including negligence), arising out of, or in connection with, your use of (including inability to use) our website.
            </p>
            <p className="text-gray-600 leading-relaxed">
              11.2 We exclude liability for any loss of profit, loss or corruption of data, special, exemplary, punitive, incidental, indirect or consequential loss or damages suffered or incurred or arising in connection with our website, any services we have provided, or in connection with any act or omission by us (negligent or otherwise).
            </p>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-4">12. Review of terms of use</h2>
            <p className="text-gray-600 leading-relaxed">
              12.1 We reserve the right to change any or all of these terms or add new terms and conditions at any time. Your continued use of our website will be deemed to constitute your acceptance of such changes.
            </p>
            <p className="text-gray-600 leading-relaxed">
              12.2 If any part of these Terms of Use are void, unenforceable or illegal, they will be severed and the remainder of these Terms of Use will continue to have full force and effect.
            </p>
            <p className="text-gray-600 leading-relaxed">
              12.3 Notices required to be given under these Terms of Use may be sent by email, by post or in the case of notices we give you, by making the information available on our website.
            </p>
            <p className="text-gray-600 leading-relaxed">
              12.4 These Terms of Use are governed by and construed under the laws of New South Wales.
            </p>
            <p className="text-gray-600 leading-relaxed">
              12.5 Any dispute or difference arising out of these Terms of Use will be submitted to arbitration in accordance with, and subject to, the Institute of Arbitrators and Mediators Australia Fast Track Arbitration Rules. The seat of arbitration will be Sydney, Australia.
            </p>

            <h2 className="text-2xl font-bold text-[#00594C] mt-10 mb-4">13. Competitions and promotions</h2>
            <p className="text-gray-600 leading-relaxed">
              13.1 From time to time we may run competitions or promotions on our website or through our social media platforms. The terms and conditions for those competitions and promotions will be specified on the relevant part(s) of the website.
            </p>
            <p className="text-gray-600 leading-relaxed">
              13.2 We retain the right, at any time and without notice, to remove, alter or add to competitions or promotions on the website.
            </p>
            <p className="text-gray-600 leading-relaxed">
              13.3 All competitions and promotions are only open to persons located in Australia and prizes will not be awarded to people who do not meet the entry requirements at the time of entry.
            </p>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfUse;
